// import APIs
import { CustomDutyLine, DutyLine } from 'RestAPIs/employees/hours-table/types';

// import consts
import { EDITABLE_FIELDS, FIELDS_TO_COPY } from './HoursTable.consts';

// import enums
import { StatusSvc } from 'components/enums';

// import external libraries
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/nl';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

// import types
import {
  CalendarRow,
  CreateBatchDutyLineValues,
  CreateFlushBatchParams,
  CreateProcessRowUpdateParams,
  ProcessedRows
} from './HoursTable.types';
import { DraftDutyLine } from 'RestAPIs/employees/hours-table/types';
import { BlankRowTemplate, DutyLineGridRow } from './HoursTable.types';
import { DutyCode, DutyCodePeriod } from 'generated/index';
import { NominalHoursOperation } from './HoursTable.enums';
import { Period } from 'components/shared/DateNavigation';
import {
  GridRowParams,
  GridRowEditStopReasons,
  MuiEvent,
  MuiBaseEvent,
  GridRowEditStopParams,
  GridCellParams,
  GridTreeNode
} from '@mui/x-data-grid-pro';

// dayjs config
dayjs.locale('nl');

export const isDraft = (row: DutyLineGridRow) => row?.requestId == null;
export const isRowRealized = (row: DutyLineGridRow) =>
  row?.statusSvc === StatusSvc.REALIZED;
export const isRowCredited = (row: DutyLineGridRow) => row?.creditDutyLineId;
export const isRowCrediting = (row: DutyLineGridRow) =>
  row?.flags?.includes('CREDIT');
export const isRowBlocked = (row: DutyLineGridRow) =>
  row.flags?.includes('STATUS_BLOCKED');
export const isRowNominal = (row: DutyLineGridRow) =>
  row.flags?.includes('HOURS_NOMINAL') || row.isNominalRow;
export const isRowApproved = (row: DutyLineGridRow) =>
  row.flags?.includes('APPROVED');
export const isRowRejected = (row: DutyLineGridRow) =>
  row.flags?.includes('REJECTED');
export const isRowPending = (row: DutyLineGridRow) =>
  row.dutyLineId &&
  (!row.flags?.includes('APPROVED') || !row.flags?.includes('REJECTED'));

export const resetDraft = (dutyLine: DraftDutyLine): DraftDutyLine => ({
  ...dutyLine,
  organizationalUnit: undefined,
  dvb: undefined,
  dutyCodeGeneral: undefined,
  dutyCodeGeneralOrganizationalUnit: undefined,
  period: '',
  dutyTime: '',
  calculatedHours: '',
  dutyType: ''
});

// required to increment the duplicate key for each new empty row created
let duplicateKey = 1;

export const createEmptyDutyLine = (template: BlankRowTemplate) => {
  const dateIso = dayjs(template.dateKey).toISOString();
  return {
    id: `${dateIso}-${duplicateKey++}`,
    dateKey: template.dateKey,
    weekNumber: template.weekNumber,
    date: template.date ?? dayjs(template.dateKey).format('YYYY-MM-DD')
  };
};

export const getPeriodsStartAndEndTime = (
  duty: DutyLineGridRow | DutyCode | CustomDutyLine
) => {
  const periods = duty.periods ?? [];
  const sortedPeriods = periods?.sort((a, b) => {
    return dayjs(a.startTime).isBefore(dayjs(b.startTime)) ? -1 : 1;
  });
  const periodFormatted = sortedPeriods?.length
    ? `${dayjs(periods[0].startTime).format('HH:mm')} – ${dayjs(
        periods[periods.length - 1].endTime
      ).format('HH:mm')}`
    : '';

  return periodFormatted;
};

export function dutyCodeToRow(duty: DutyCode | CustomDutyLine) {
  // convert the raw periods into the DTO the grid already uses
  const periods: DutyCodePeriod[] = duty.periods.map((period) => ({
    dutyCodePeriodId: period.dutyCodePeriodId,
    startTime: period.startTime,
    endTime: period.endTime,
    dutyHourType: period.dutyHourType
  }));

  const dutyTime = periods
    .reduce(
      (sum, period) =>
        sum +
        dayjs(period.endTime).diff(dayjs(period.startTime), 'minute') / 60,
      0
    )
    .toFixed(2);

  const dutyType = periods
    .map((period) => period?.dutyHourType && period?.dutyHourType.code)
    .join(', ');

  return { periods, dutyTime, dutyType };
}

// build an array with one skeleton-row per calendar day of the month
export const generateMonthRows = (selectedDate: Dayjs): CalendarRow[] => {
  const start = dayjs(selectedDate).startOf('month');
  const end = dayjs(selectedDate).endOf('month');

  const rows: CalendarRow[] = [];
  let current = start;

  while (current.isBefore(end) || current.isSame(end, 'day')) {
    rows.push(rowForSpecificDate(current));
    current = current.add(1, 'day');
  }

  return rows;
};

// build an array with one skeleton-row per calendar day of the week
export const generateWeekRows = (selectedDate: Dayjs): CalendarRow[] => {
  const start = dayjs(selectedDate).startOf('week'); // Monday
  const rows: CalendarRow[] = [];

  for (let i = 0; i < 7; i++) {
    rows.push(rowForSpecificDate(start.add(i, 'day')));
  }

  return rows;
};

// create a row for a specific date
const rowForSpecificDate = (date: Dayjs): CalendarRow => {
  const dateKey = date.format('YYYY-MM-DD');
  return {
    id: dayjs(dateKey).toISOString(),
    dateKey,
    weekNumber: date.isoWeek(),
    date: date.format('dd, DD-MM-YYYY')
  };
};

// Convert an array of API duty lines into a fast key-lookup by “YYYY-MM-DD”
export function toDutyLineMap(dutyLines: DutyLine[]) {
  return dutyLines.reduce<Record<string, DutyLine>>((acc, dutyLine) => {
    const key = dayjs(dutyLine.dutyDate).format('YYYY-MM-DD');
    acc[key] = dutyLine;
    return acc;
  }, {});
}

// extract only the fields we copy / paste
export function stripForCopy<T extends Record<string, any>>(row: T) {
  // uses the const-asserted tuple so TS knows the keys are valid
  return (FIELDS_TO_COPY as readonly string[]).reduce(
    (acc, k) => ({ ...acc, [k]: row[k] }),
    {}
  ) as Partial<T>;
}

// change the base date and keep the time of the original date
const changeDateKeepTime = (originalDateStr: string, newDateStr: string) => {
  const original = dayjs(originalDateStr).utc();
  return dayjs
    .utc(newDateStr)
    .hour(original.hour())
    .minute(original.minute())
    .second(original.second())
    .millisecond(original.millisecond())
    .toISOString();
};

// normalize periods to match the API expectations
export const preparePeriodsForApi = (raw?: CustomDutyLine['periods']) => {
  return (raw ?? []).map((period) => {
    const startTime = dayjs(period.startTime);
    const endTime = dayjs(period.endTime);
    // Equal start/end times represent a full 24-hour duty period.
    const endsOnNextDay = !endTime.isAfter(startTime);
    const normalizedEndTime = endsOnNextDay ? endTime.add(1, 'day') : endTime;

    const endTimeBaseDate = endsOnNextDay ? '1970-01-02' : '1970-01-01';

    return {
      startTime: changeDateKeepTime(period.startTime, '1970-01-01'),
      endTime: changeDateKeepTime(
        normalizedEndTime.toISOString(),
        endTimeBaseDate
      ),
      hours: normalizedEndTime.diff(startTime, 'minute') / 60,
      dutyHourType: {
        id: period.dutyHourType?.id ?? period.dutyHourType?.dutyHourTypeId
      }
    };
  });
};

export const toOptionalNumber = (value: unknown): number | undefined => {
  if (value === null) return undefined;
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

export const buildCreateValue = (
  row: CustomDutyLine,
  employeeId: number,
  newDate: string
) => ({
  costCenterId: row.brId,
  date: dayjs(newDate).toISOString(),
  employeeId,
  plannedDutyId: row.dutyId,
  workAgreementId: row.waId,
  flags: row.flags,
  periods: preparePeriodsForApi(row.periods)
});

export const overWriteCreateValue = (
  row: CustomDutyLine,
  employeeId: number,
  newDate: string,
  oldRow: CreateBatchDutyLineValues
) => {
  return {
    costCenterId:
      oldRow.costCenterId !== row.organizationalUnit
        ? row.organizationalUnit
        : row.brId,
    date: dayjs(newDate).toISOString(),
    employeeId,
    plannedDutyId:
      typeof row.displayDutyCode === 'number'
        ? row.displayDutyCode
        : row.dutyId,
    workAgreementId: row.dvb === row.workAgreementNr ? row.waId : row.dvb,
    flags: row.flags,
    periods: preparePeriodsForApi(row.periods)
  };
};

export const buildUpdateValue = (row: DutyLine | CustomDutyLine) => ({
  dutyLineId: row.dutyLineId,
  costCenterId: row.brId,
  plannedDutyId: row.dutyId,
  waId: row.waId,
  flags: row.flags,
  periods: row.periods
});

export const statusApproval = (row: DutyLineGridRow) => {
  const item = [
    {
      visible: !!isRowApproved(row),
      icon: 'check',
      color: 'var(--mui-palette-primary-main)'
    },
    {
      visible: !!isRowPending(row),
      icon: 'hourglass-half',
      color: 'var(--mui-palette-dawn-onFixedVariant)'
    },
    {
      visible: !!isRowRejected(row),
      icon: 'xmark',
      color: 'var(--mui-palette-error-main)'
    }
  ].find((status) => status.visible);

  return item;
};

export const collectUntouchedOriginals = (
  dutyLines: CustomDutyLine[],
  processedRows: ProcessedRows
) => {
  const untouched = new Map<
    string | number,
    ReturnType<typeof buildUpdateValue>
  >();

  dutyLines.forEach((row) => {
    // skip rows turned into drafts or removed
    if (
      processedRows.deletedRows.has(row.dutyLineId) || // user deleted
      processedRows.createdRows.has(row.id) || // recreated
      processedRows.updatedRows.has(row.dutyLineId) // edited
    ) {
      return;
    }

    untouched.set(
      `${dayjs(row.dutyDate).toDate().toISOString()}-${row.dutyLineId}`, // what BE expects
      buildUpdateValue(row)
    );
  });
  return untouched;
};

export function createProcessRowUpdate({
  copyMode,
  employeeId,
  isEmployeePortal,
  setProcessedRows,
  setOverrides,
  createOrUpdateNominalHoursDutyLine,
  createDutyLine,
  updateDutyLine
}: CreateProcessRowUpdateParams) {
  return async function processRowUpdate(newRow: any, oldRow: any) {
    if (JSON.stringify(newRow) === JSON.stringify(oldRow)) return newRow;

    // nominal
    if (isRowNominal(newRow)) {
      const nominalHoursPayload = {
        employeeId,
        isEmployeePortal,
        requestId: newRow.requestId,
        operation: newRow.requestId
          ? NominalHoursOperation.UPDATE
          : NominalHoursOperation.CREATE,
        body: {
          date: dayjs(newRow.dateKey).toDate().toISOString(),
          dutyTypeId:
            typeof newRow.dutyType === 'string'
              ? newRow.nominalDutyTypeId
              : newRow.dutyType,
          hours: toOptionalNumber(newRow.dutyTime) ?? 0,
          workAgreementId:
            newRow.dvb === newRow.workAgreementNr ? newRow.waId : newRow.dvb
        }
      };
      await createOrUpdateNominalHoursDutyLine(nominalHoursPayload);
      return newRow;
    }

    // copy mode batching
    if (copyMode) {
      setProcessedRows((prev) => {
        const next = { ...prev };
        const { id, requestId, dateKey } = newRow;

        if (next.createdRows.has(id)) {
          const prevRow = next.createdRows.get(id);
          next.createdRows.set(
            id,
            overWriteCreateValue(newRow, employeeId, dateKey, prevRow)
          );
          return next;
        }
        if (next.updatedRows.has(id)) {
          next.updatedRows.set(id, buildUpdateValue(newRow));
          return next;
        }

        const isCreate = requestId === null;
        const targetMap = isCreate ? next.createdRows : next.updatedRows;
        targetMap.set(
          id,
          isCreate
            ? buildCreateValue(newRow, employeeId, dateKey)
            : buildUpdateValue(newRow)
        );
        return next;
      });

      // we correct overrides to ensure the pasted row is not overridden
      setOverrides((prev) => new Map(prev).set(newRow.id, newRow));
      return newRow;
    }

    // draft creation or update
    if (isDraft(newRow)) {
      const createPayload = {
        employeeId,
        isEmployeePortal,
        body: {
          costCenterId: newRow.organizationalUnit,
          date: dayjs(newRow.dateKey).toDate().toISOString(),
          employeeId,
          plannedDutyId: newRow.displayDutyCode,
          workAgreementId: newRow.dvb,
          flags: newRow.flags,
          periods: preparePeriodsForApi(newRow.periods)
        }
      };
      const created = await createDutyLine(createPayload);
      return { ...newRow, requestId: created.request, id: created.request };
    }

    // persist update
    const updatePayload = {
      employeeId,
      requestId: newRow.requestId,
      isEmployeePortal,
      body: {
        costCenterId: newRow.organizationalUnit,
        date: dayjs(newRow.dateKey).toDate().toISOString(),
        employeeId,
        plannedDutyId:
          oldRow.displayDutyCode === newRow.displayDutyCode
            ? newRow.dutyId
            : newRow.displayDutyCode,
        waId: oldRow.dvb === newRow.dvb ? newRow.waId : newRow.dvb,
        flags: newRow.flags,
        periods: preparePeriodsForApi(newRow.periods)
      }
    };
    await updateDutyLine(updatePayload);
    return newRow;
  };
}

export function buildBatchBody(
  processed: ProcessedRows,
  everythingToUpdate: Map<any, any>
) {
  return {
    toCreate: Array.from(processed.createdRows, ([rowIndex, value]) => ({
      rowIndex,
      value
    })),
    toUpdate: Array.from(everythingToUpdate, ([rowIndex, value]) => ({
      rowIndex,
      value
    })),
    toDelete: Array.from(processed.deletedRows)
  };
}

export function createFlushBatch({
  processedRows,
  dutyLines,
  employeeId,
  isEmployeePortal,
  cudBatchDutyLines,
  resetCopyState
}: CreateFlushBatchParams) {
  const collect = () => collectUntouchedOriginals(dutyLines, processedRows);

  return async function flushBatch() {
    const everythingToUpdate = new Map([
      ...processedRows.updatedRows, // the rows the user edited
      ...collect() // every untouched persisted row
    ]);

    const body = buildBatchBody(processedRows, everythingToUpdate);

    if (
      body.toCreate.length === 0 &&
      body.toUpdate.length === 0 &&
      body.toDelete.length === 0
    ) {
      resetCopyState();
      return;
    }

    await cudBatchDutyLines({
      employeeId,
      isEmployeePortal,
      body
    });

    resetCopyState();
  };
}

export function buildRowClassName(
  params: GridRowParams<DutyLineGridRow>,
  activeRowId: string | number | null,
  copiedRowId: string | number | null
) {
  const weekend = [0, 6].includes(dayjs(params.row.dateKey).day())
    ? 'weekend-row'
    : '';
  const blocked = isRowBlocked(params.row) ? 'blocked-row' : '';
  const activeRow = params.id === activeRowId ? 'active-row' : '';
  const isClipboard = copiedRowId === params.row.id ? 'clipboard-row' : '';
  return [weekend, isClipboard, activeRow, blocked].filter(Boolean).join(' ');
}

export function isCellEditable({
  copyMode,
  isEmployeePortal
}: {
  copyMode: boolean;
  isEmployeePortal: boolean;
}) {
  return function isCellEditable({ row, field }) {
    if (
      isRowRealized(row) ||
      isRowCrediting(row) ||
      isRowBlocked(row) ||
      (copyMode && isRowNominal(row)) ||
      isEmployeePortal
    )
      return false;

    const allowed = isRowNominal(row)
      ? EDITABLE_FIELDS.nominal
      : EDITABLE_FIELDS.other;
    return allowed.has(field);
  };
}

export const tableTotalHours = (rows: DutyLineGridRow[]) =>
  rows?.reduce((acc, row) => acc + (row.totalHours ?? 0), 0).toFixed(2);

export function computeRange(
  selectedDate: Dayjs | string | Date | null,
  period: Period | null
) {
  const base = dayjs(selectedDate);
  if (period === 'week') {
    return {
      startDate: base.startOf('week').toISOString(),
      endDate: base.endOf('week').toISOString()
    };
  }
  return {
    startDate: base.startOf('month').toISOString(),
    endDate: base.endOf('month').toISOString()
  };
}

export type onRowEditStartParams = {
  params: GridRowParams<DutyLineGridRow>;
  event: MuiEvent<React.KeyboardEvent | React.MouseEvent>;
  isRowEditModeActive: boolean;
  editingRowId: string | number | null;
  setIsRowEditModeActive: React.Dispatch<React.SetStateAction<boolean>>;
  setEditingRowId: React.Dispatch<React.SetStateAction<string | number | null>>;
};

export const onRowEditStart = ({
  params,
  event,
  isRowEditModeActive,
  editingRowId,
  setIsRowEditModeActive,
  setEditingRowId
}: onRowEditStartParams) => {
  if (isRowEditModeActive && editingRowId !== params.id) {
    event.defaultMuiPrevented = true;
    return;
  }

  setIsRowEditModeActive(true);
  setEditingRowId(params.id);
};

export type OnRowEditStopParams = {
  params: GridRowEditStopParams<DutyLineGridRow>;
  event: MuiEvent<MuiBaseEvent>;
  setIsRowEditModeActive: React.Dispatch<React.SetStateAction<boolean>>;
  setEditingRowId: React.Dispatch<React.SetStateAction<string | number | null>>;
};

export const onRowEditStop = ({
  params,
  event,
  setIsRowEditModeActive,
  setEditingRowId
}: OnRowEditStopParams) => {
  // prevent row edit to commit on focus out or Enter key press
  if (
    params.reason === GridRowEditStopReasons.rowFocusOut ||
    (event && 'key' in event && event.key === 'Enter' && !event.shiftKey)
  ) {
    event.defaultMuiPrevented = true;
    return;
  }

  setIsRowEditModeActive(false);
  setEditingRowId(null);
};

export const isRowSelectableFromParent = (
  params: GridRowParams<DutyLineGridRow>
) => {
  if (isRowNominal(params.row)) return true;
  return (
    params.row.organizationalUnit != null &&
    params.row.dvb != null &&
    !isRowRealized(params.row) &&
    !isRowCrediting(params.row) &&
    !isRowBlocked(params.row)
  );
};

export const onCellKeyDown = ({
  params,
  event,
  copyMode,
  copySourceRowId,
  setCopySourceRowId,
  setCopyPayload,
  setCopyMode,
  pasteOntoRow,
  resetCopyState,
  undoPaste,
  isRowEditModeActive,
  pastedRows,
  clearRow,
  setProcessedRows,
  handleFinishCopy
}: {
  params: GridCellParams<any, unknown, unknown, GridTreeNode>;
  event: React.KeyboardEvent;
  copyMode: boolean;
  copySourceRowId: string | number | null;
  setCopySourceRowId: React.Dispatch<
    React.SetStateAction<string | number | null>
  >;
  setCopyPayload: React.Dispatch<React.SetStateAction<any>>;
  setCopyMode: React.Dispatch<React.SetStateAction<boolean>>;
  pasteOntoRow: (row: CustomDutyLine) => void;
  resetCopyState: () => void;
  undoPaste: (row: CustomDutyLine) => void;
  isRowEditModeActive: boolean;
  pastedRows: Set<string | number>;
  clearRow: (row: CustomDutyLine) => void;
  setProcessedRows: React.Dispatch<React.SetStateAction<ProcessedRows>>;
  handleFinishCopy: () => void;
}) => {
  // copy on command/ctrl + c
  if (
    !isRowEditModeActive &&
    event.key === 'c' &&
    (event.metaKey || event.ctrlKey)
  ) {
    event.preventDefault();
    setCopySourceRowId(params.id);
    setCopyPayload(stripForCopy(params.row));
    setCopyMode(true);
    event.stopPropagation();
  }

  // paste on command/ctrl + v
  if (
    copyMode &&
    !!copySourceRowId &&
    event.key === 'v' &&
    (event.metaKey || event.ctrlKey)
  ) {
    event.preventDefault();
    pasteOntoRow(params.row);
    event.stopPropagation();
  }

  // reset copy mode on escape key
  if (copyMode && event.key === 'Escape') {
    event.preventDefault();
    resetCopyState();
    event.stopPropagation();
  }

  // undo paste on command/ctrl + z
  if (
    copyMode &&
    event.key === 'z' &&
    (event.metaKey || event.ctrlKey) &&
    pastedRows.size > 0
  ) {
    event.preventDefault();
    undoPaste(params.row);
    event.stopPropagation();
  }

  // listen for ctrl / command + d to delete existing rows in copy mode
  if (
    copyMode &&
    event.key === 'd' &&
    (event.metaKey || event.ctrlKey) &&
    !isRowEditModeActive &&
    params.row.requestId &&
    !isRowCrediting(params.row)
  ) {
    event.preventDefault();
    clearRow(params.row);
    setProcessedRows((prev) => {
      const next = { ...prev };
      params.row.requestId && next.deletedRows.add(params.row.requestId);
      next.createdRows.delete(params.row.id);
      next.updatedRows.delete(params.row.id);
      return next;
    });
    event.stopPropagation();
  }

  // finish copy-paste on command/ctrl + enter
  if (copyMode && event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
    handleFinishCopy();
    event.stopPropagation();
  }
};
